import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

interface Stat {
    /** Numeric value shown in mono (or formatted number for price). */
    value: React.ReactNode;
    /** Localized label rendered below the value. */
    label: React.ReactNode;
    key: string;
}

interface Props {
    /** ID of the `<form>` element this bar submits. */
    formId: string;
    /** Disables the primary save button (used by validation/loading gates). */
    saveDisabled: boolean;
    /** Toggles "Krijo artikullin" vs "Ruaj ndryshimet". */
    mode: 'create' | 'edit';
    /** Cancel handler — usually navigates back to the list. */
    onCancel: () => void;
    /** Loading flag — disables both buttons during submit. */
    isLoading: boolean;
    /** Number of selected processes (responsibilities). */
    processesCount: number;
    /** Number of selected materials. */
    materialsCount: number;
    /** Unit price; rendered as euro currency (€) with thousand-separators. Empty string = dash. */
    price: number | '';
}

/**
 * ArticleSaveBar — sticky bottom action bar for create/edit screens.
 *
 * Pins to the bottom of the viewport so the user always has a save target,
 * no matter how long the form scroll gets. Left side shows live summary
 * stats (Procese, Materiale, Çmimi); right side has Cancel + primary Save.
 *
 * Submission flow: the form lives in `ArticleForm` and has `id={formId}`;
 * the save `<button type="submit" form={formId}>` submits it natively
 * without needing a lifted handler.
 */
const ArticleSaveBar: React.FC<Props> = ({
    formId,
    saveDisabled,
    mode,
    onCancel,
    isLoading,
    processesCount,
    materialsCount,
    price,
}) => {
    const stats: Stat[] = [
        {
            key: 'processes',
            value: <span className="mono">{processesCount}</span>,
            label: <FormattedMessage id="admin.articles.saveBar.processes" defaultMessage="Procese" />,
        },
        {
            key: 'materials',
            value: <span className="mono">{materialsCount}</span>,
            label: <FormattedMessage id="admin.articles.saveBar.materials" defaultMessage="Materiale" />,
        },
        {
            key: 'price',
            value: (
                <span className="mono">
                    {price === '' ? '—' : <FormattedNumber value={Number(price)} style="currency" currency="EUR" />}
                </span>
            ),
            label: <FormattedMessage id="admin.articles.saveBar.priceAll" defaultMessage="Çmimi" />,
        },
    ];

    return (
        <div className="art-save-bar" role="region" aria-label="Save bar">
            <div className="art-save-bar__stats" aria-hidden={false}>
                {stats.map((stat, i) => (
                    <React.Fragment key={stat.key}>
                        {i > 0 && <span className="art-save-bar__divider" aria-hidden="true" />}
                        <div className="art-save-bar__stat">
                            <span className="art-save-bar__stat-value">{stat.value}</span>
                            <span className="art-save-bar__stat-label">{stat.label}</span>
                        </div>
                    </React.Fragment>
                ))}
            </div>

            <div className="art-save-bar__actions">
                <button
                    type="button"
                    className="art-btn art-btn--ghost"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    <FormattedMessage id="admin.articles.form.cancel" defaultMessage="Anulo" />
                </button>
                <button
                    type="submit"
                    form={formId}
                    className="art-btn art-btn--primary art-save-bar__save"
                    disabled={saveDisabled}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                    </svg>
                    {mode === 'create' ? (
                        <FormattedMessage id="admin.articles.btnCreate" defaultMessage="Krijo artikullin" />
                    ) : (
                        <FormattedMessage id="admin.articles.saveBar.saveChanges" defaultMessage="Ruaj ndryshimet" />
                    )}
                </button>
            </div>
        </div>
    );
};

export default ArticleSaveBar;
